import isObject from 'lodash/isObject';
import applicationPlatform, { NativeInterface } from '../platform';
import { isJSON } from './stringHelper';
import UAHelper from './uaHelper';
import { kevlarResponseConstants } from '../constants';
import { IRichViewBridgeParams } from '../platform/definitions';
import { IAuthParams } from '../types';
import { FDPEvent, NavigationContext } from '../tracking/types';

const {
  MAX_RETRY_REFRESH_AT_COUNT,
  REFRESH_TOKEN_API_TIMEOUT
} = kevlarResponseConstants;

export interface IATToken {
  at: string;
}
class BridgeCaller {
  static async _request({
    method,
    params,
    isSetupCallbackRequiredForAndroid = false
  }: IRichViewBridgeParams): Promise<any> {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const data = await NativeInterface.invoke({
            method,
            params,
            isSetupCallbackRequiredForAndroid
          });
          if (isJSON(data)) {
            try {
              resolve(JSON.parse(data));
            } catch (error) {
              reject('Invalid JSON Response');
            }
          } else {
            resolve(data);
          }
        } catch (error) {
          reject(error instanceof Error ? error.message : error);
        }
      })();
    });
  }
}

export async function getRefreshAT(retryCount: number): Promise<IATToken> {
  let timeoutId: NodeJS.Timeout;

  const refreshATTimeout = new Promise((_, timeoutReject) => {
    timeoutId = setTimeout(() => {
      // for better debugging
      console.log('getRefreshAT timeout');
      timeoutReject('getRefreshAT timeout');
    }, REFRESH_TOKEN_API_TIMEOUT * 1000);
  });
  try {
    return await Promise.race([
      refreshATTimeout,
      BridgeCaller._request({
        method: 'getRefreshAT',
        isSetupCallbackRequiredForAndroid: true
      })
    ]).then(data => {
      //clear timeout if we got a response
      timeoutId && clearTimeout(timeoutId);
      return data;
    });
  } catch (exception) {
    return retryCount < MAX_RETRY_REFRESH_AT_COUNT
      ? getRefreshAT(retryCount + 1)
      : Promise.reject(exception);
  }
}

export function getAuthParams(): Promise<IAuthParams> {
  return BridgeCaller._request({ method: 'getAuthParams' }).then(
    data => data,
    _ => window.appEnvironment
  );
}
export function getMAPIDataCenterID(): Promise<string> {
  return BridgeCaller._request({ method: 'getMAPIDataCenterID' }).then(
    data => data,
    _ => '1'
  );
}

export function getSecureMapiUrl() {
  return BridgeCaller._request({ method: 'getSecureMapiUrl' }).then(
    (data: { url: string } | string) => {
      if (isObject(data)) {
        return data.url;
      } else {
        return data;
      }
    },
    _ => 'https://www.flipkart.com/api'
  );
}

export function getNavigationContext(): Promise<NavigationContext> {
  return BridgeCaller._request({ method: 'getNavigationContext' }).then(
    data => data,
    () => ({ pn: '', pt: '' })
  );
}

export function updateCurrentNavigationContextInfo(value: NavigationContext) {
  const params = UAHelper.isAndroid() ? JSON.stringify(value) : value;
  return BridgeCaller._request({
    method: 'updateCurrentNavigationContextInfo',
    params
  });
}

export function ingestEvent(event: FDPEvent) {
  const params = UAHelper.isAndroid() ? JSON.stringify(event) : event;
  return BridgeCaller._request({ method: 'ingestEvent', params });
}

export function pushEvents() {
  return BridgeCaller._request({ method: 'pushEvents' });
}

export function changeToolBarVisibility(
  hideToolBar: boolean,
  addTopSafeAreaForCustomToolBar?: boolean
) {
  const params = UAHelper.isAndroid()
    ? hideToolBar
    : {
        hidden: hideToolBar,
        addTopSafeAreaForCustomToolBar: !!addTopSafeAreaForCustomToolBar
      };
  return BridgeCaller._request({ method: 'changeToolBarVisibility', params });
}
